import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useLayoutEffect, useRef } from "react";

import { Merry3DCard } from "@/shared/ui/Merry3DCard";

gsap.registerPlugin(useGSAP);

export interface MerryAssetHoverPreviewProps {
  alt: string;
  anchor: HTMLElement;
  host: HTMLElement;
  src: string;
}

const previewBounds = 206.858;
const viewportPadding = 12;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function MerryAssetHoverPreview({
  alt,
  anchor,
  host,
  src,
}: MerryAssetHoverPreviewProps) {
  const positionerRef = useRef<HTMLDivElement>(null);
  const presenceRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const positioner = positionerRef.current;
    if (!positioner) return;

    const updatePosition = () => {
      if (!anchor.isConnected || !host.isConnected) {
        positioner.style.visibility = "hidden";
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const half = previewBounds / 2;
      const viewportCenterX = clamp(
        anchorRect.left + anchorRect.width / 2,
        viewportPadding + half,
        window.innerWidth - viewportPadding - half,
      );
      const viewportCenterY = clamp(
        anchorRect.top + anchorRect.height / 2,
        viewportPadding + half,
        window.innerHeight - viewportPadding - half,
      );

      positioner.style.left = `${viewportCenterX - hostRect.left}px`;
      positioner.style.top = `${viewportCenterY - hostRect.top}px`;
      positioner.style.visibility = "visible";
    };

    updatePosition();
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    resizeObserver.observe(host);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchor, host]);

  useGSAP(
    () => {
      const presence = presenceRef.current;
      if (!presence) return;

      gsap.killTweensOf(presence);
      if (prefersReducedMotion()) {
        gsap.set(presence, { autoAlpha: 1, scale: 1 });
        return;
      }

      gsap.set(presence, { willChange: "opacity,transform" });
      gsap.fromTo(
        presence,
        { autoAlpha: 0, scale: 0.92 },
        {
          autoAlpha: 1,
          duration: 0.22,
          ease: "power3.out",
          overwrite: true,
          scale: 1,
          onComplete: () =>
            gsap.set(presence, {
              clearProps: "opacity,visibility,willChange",
            }),
        },
      );
    },
    { dependencies: [src], scope: positionerRef },
  );

  return (
    <div
      aria-hidden="true"
      className="merry-asset-hover-preview"
      data-figma-source-node-id="571:60149"
      data-testid="merry-asset-hover-preview"
      ref={positionerRef}
      style={{ visibility: "hidden" }}
    >
      <div
        className="merry-asset-hover-preview__presence"
        ref={presenceRef}
      >
        <div className="merry-asset-hover-preview__base-transform">
          <Merry3DCard interactionTarget={anchor}>
            <span
              className="merry-asset-hover-preview__surface"
              data-figma-node-id="571:60164"
            >
              <img alt={alt} draggable="false" src={src} />
              <span className="merry-asset-hover-preview__glare" />
            </span>
          </Merry3DCard>
        </div>
      </div>
    </div>
  );
}
